import "./ui/styles.css";
import { el } from "./ui/dom";
import { effect } from "./ui/signals";
import { startRouter, currentPath } from "./ui/router";

import { renderHome } from "./pages/home";
import { renderAbout } from "./pages/about";
import { renderBloom } from "./pages/bloom";
import { renderCuckoo } from "./pages/cuckoo";
import { renderHyperLogLog } from "./pages/hyperloglog";
import { renderCountMin } from "./pages/count-min";
import { renderMinHash } from "./pages/minhash";
import { renderReservoir } from "./pages/reservoir";
import { renderDDSketch } from "./pages/dd-sketch";
import { renderTopK } from "./pages/top-k";
import type { PageRenderer } from "./ui/router";

interface NavLink {
  path: string;
  label: string;
  ico: string;
  tag?: string;
}
interface NavGroup {
  group: string;
  links: NavLink[];
}

const NAV: NavGroup[] = [
  {
    group: "Start here",
    links: [
      { path: "/", label: "Decision guide", ico: "◎" },
      { path: "/about", label: "About", ico: "❔" },
    ],
  },
  {
    group: "Is x in the set?",
    links: [
      { path: "/bloom", label: "Bloom filter", ico: "▦", tag: "member" },
      { path: "/cuckoo", label: "Cuckoo filter", ico: "▤", tag: "member±" },
    ],
  },
  {
    group: "How many, how often, how big?",
    links: [
      { path: "/hyperloglog", label: "HyperLogLog", ico: "◔", tag: "distinct" },
      { path: "/count-min", label: "Count-Min Sketch", ico: "▩", tag: "freq" },
      { path: "/top-k", label: "Top-K", ico: "↥", tag: "top-k" },
      { path: "/dd-sketch", label: "DDSketch", ico: "◑", tag: "p99" },
    ],
  },
  {
    group: "Similar? representative?",
    links: [
      { path: "/minhash", label: "MinHash + LSH", ico: "⧉", tag: "similar" },
      { path: "/reservoir", label: "Reservoir sampling", ico: "◍", tag: "sample" },
    ],
  },
];

const routes: Record<string, PageRenderer> = {
  "/": renderHome,
  "/about": renderAbout,
  "/bloom": renderBloom,
  "/cuckoo": renderCuckoo,
  "/hyperloglog": renderHyperLogLog,
  "/count-min": renderCountMin,
  "/minhash": renderMinHash,
  "/reservoir": renderReservoir,
  "/dd-sketch": renderDDSketch,
  "/top-k": renderTopK,
};

const notFound: PageRenderer = (outlet) => {
  outlet.appendChild(
    el(
      "div",
      { class: "page-header" },
      el("h1", { class: "page-title" }, "Not found"),
      el("p", { class: "lede" }, "That page doesn't exist. "),
      el("a", { href: "#/" }, "← Back to the decision guide"),
    ),
  );
};

// --- Theme -----------------------------------------------------------------

function initTheme(): () => void {
  const KEY = "probably-theme";
  const stored = localStorage.getItem(KEY);
  const apply = (t: string) => document.documentElement.setAttribute("data-theme", t);
  apply(stored ?? "dark");
  return () => {
    const next = document.documentElement.getAttribute("data-theme") === "dark" ? "light" : "dark";
    apply(next);
    localStorage.setItem(KEY, next);
    themeBtn.textContent = next === "dark" ? "☾ Dark" : "☀ Light";
  };
}

// --- Build the shell -------------------------------------------------------

const navLinkEls = new Map<string, HTMLElement>();

function buildNav(): HTMLElement {
  const nav = el("nav", { class: "nav" });
  for (const g of NAV) {
    nav.appendChild(el("div", { class: "nav-group-label" }, g.group));
    for (const link of g.links) {
      const a = el(
        "a",
        { class: "nav-link", href: `#${link.path}` },
        el("span", { class: "nav-ico", "aria-hidden": "true" }, link.ico),
        el("span", {}, link.label),
        link.tag ? el("span", { class: "tag" }, link.tag) : null,
      );
      navLinkEls.set(link.path, a);
      nav.appendChild(a);
    }
  }
  return nav;
}

const toggleTheme = initTheme();
const themeBtn = el(
  "button",
  { class: "btn btn-ghost", type: "button", onclick: () => toggleTheme() },
  document.documentElement.getAttribute("data-theme") === "dark" ? "☾ Dark" : "☀ Light",
);

const outlet = el("div", { class: "outlet" });

const sidebar = el(
  "aside",
  { class: "sidebar" },
  el(
    "a",
    { class: "brand", href: "#/", style: { textDecoration: "none", color: "var(--text)" } },
    el("span", {}, "🎲"),
    el("span", {}, "Probably", el("small", {}, "probabilistic data structures")),
  ),
  buildNav(),
  el(
    "div",
    { class: "sidebar-footer" },
    themeBtn,
    el("span", {}, "MIT · offline"),
  ),
);

const skipLink = el(
  "a",
  {
    class: "skip-link",
    href: "#",
    onclick: (e: Event) => {
      e.preventDefault();
      const main = document.getElementById("main");
      main?.focus();
      main?.scrollIntoView();
    },
  },
  "Skip to content",
);

const app = el(
  "div",
  { class: "app" },
  skipLink,
  sidebar,
  el("main", { class: "content", id: "main", tabindex: "-1" }, outlet),
);

document.getElementById("app")!.replaceWith(app);

// Highlight the active nav link reactively.
effect(() => {
  const path = currentPath.value;
  for (const [p, node] of navLinkEls) node.classList.toggle("active", p === path);
});

startRouter(outlet, routes, notFound);
